import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL, mediaUrl } from './config';
import { formatCurrency } from './currency';
import {
  IconCart, IconPackage, IconArrowRight, IconAlertTriangle, IconCheckCircle, IconShieldCheck,
} from './Icons';

// Where an abandoned-cart recovery email lands.
//
// The customer clicks a link in their inbox and arrives here with nothing but
// a token. This page's whole job is to turn that into "your cart is back, here
// is what changed, continue to checkout" in as few steps as possible.
//
// ── Why it renders before asking anyone to sign in ──────────────────────────
// The old reminder email ended with "log back in to AcyMart to pick up where
// you left off", which puts a login wall between a customer and the thing they
// were already told they had. GET /api/cart-recovery/:token is deliberately
// public and returns no identifying information, so the cart can be SHOWN to
// whoever holds the link. Writing it back still requires being signed in as
// the owner -- the API enforces that, not this component.
//
// ── How the destination survives a login ────────────────────────────────────
// By never leaving. If the customer has to sign in, the auth modal opens over
// this page and the URL does not change, so when it closes they are still on
// their saved cart with the token intact and the restore they asked for is
// carried out immediately. There is no returnTo parameter to lose, and no
// redirect chain to get wrong.
//
// ── Styling ─────────────────────────────────────────────────────────────────
// Reuses the checkout's own class names (.co-panel, .fi-item, .price-row,
// .btn-place-order) rather than introducing a parallel set. Those already have
// full desktop and mobile treatments -- including everything in
// frontend/src/mobile/ -- so this page inherits the phone layout for free and
// cannot drift away from the checkout it leads into.

const ChangeNotice = ({ changes, removed }) => {
  if (!changes.length && !removed.length) return null;

  return (
    <div className="co-panel" style={{ marginBottom: 16 }}>
      <h3 className="co-panel-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <IconAlertTriangle size={16} /> Some things changed while you were away
      </h3>
      {/* Disclosed here, before the customer commits, rather than at the
          payment step. A price that moves between the email and the order is
          not a surprise anyone should discover after entering their details. */}
      <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.8 }}>
        {changes.map((change) => (
          <li key={`${change.productId}-${change.type}`}>
            <strong>{change.name}</strong>{' '}
            {change.type === 'price'
              ? <>is now {formatCurrency(change.to)} (was {formatCurrency(change.from)})</>
              : <>is limited to {change.to} (you had {change.from})</>}
          </li>
        ))}
        {removed.map((item) => (
          <li key={`removed-${item.productId}`}>
            <strong>{item.name || 'An item'}</strong>{' '}
            {item.reason === 'out_of_stock' ? 'has sold out and was removed' : 'is no longer available and was removed'}
          </li>
        ))}
      </ul>
    </div>
  );
};

const CartLine = ({ item, onNavigateToProduct }) => (
  <div className="fi-item">
    <div className="fi-content-row">
      <div className="fi-left">
        {item.image ? (
          <img
            src={mediaUrl(item.image)}
            alt={item.name}
            className="fi-img"
            onClick={() => onNavigateToProduct?.(item.productId)}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          // Matches ProductImage.js's behaviour for a missing photo: a neutral
          // icon rather than a broken-image glyph.
          <div className="fi-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-tertiary)' }}>
            <IconPackage size={24} style={{ color: 'var(--text-tertiary)' }} />
          </div>
        )}
      </div>
      <div className="fi-right">
        <p
          className="fi-name"
          onClick={() => onNavigateToProduct?.(item.productId)}
          style={{ cursor: 'pointer' }}
        >
          {item.name}
        </p>
        {item.brand && <p className="fi-size">{item.brand}</p>}
        {item.color && <p className="fi-size">Colour: <strong>{item.color}</strong></p>}
        {item.size && <p className="fi-size">Size: <strong>{item.size}</strong></p>}
        <div className="fi-price-row">
          {item.wasPrice > item.unitPrice && (
            <span className="fi-mrp">{formatCurrency(item.wasPrice)}</span>
          )}
          <span className="fi-price">{formatCurrency(item.unitPrice)}</span>
        </div>
        {item.quantity > 1 && (
          <p className="fi-line-total">
            {item.quantity} × {formatCurrency(item.unitPrice)} ={' '}
            <strong>{formatCurrency(item.unitPrice * item.quantity)}</strong>
          </p>
        )}
      </div>
    </div>
  </div>
);

export default function CartRecoveryPage({
  token,
  user,
  onRestored,
  onContinueToCheckout,
  onSignIn,
  onNavigateToProduct,
  onGoHome,
}) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null, reason: null });
  const [restoring, setRestoring] = useState(false);
  // Set when someone presses the button while signed out. Once they finish
  // signing in, the restore they already asked for is carried out without
  // making them press it a second time.
  const pendingRestoreRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', data: null, error: null, reason: null });

    fetch(`${API_BASE_URL}/api/cart-recovery/${token}`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const err = new Error(data.error || 'This cart link is no longer available.');
          err.reason = data.reason || 'invalid';
          throw err;
        }
        return data;
      })
      .then((data) => { if (!cancelled) setState({ status: 'ready', data, error: null, reason: null }); })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error', data: null, error: err.message, reason: err.reason });
      });

    return () => { cancelled = true; };
    // Re-runs when the visitor signs in, so `isOwner` reflects the new session
    // rather than the anonymous one the page first loaded with.
  }, [token, user]);

  const restore = useCallback(async () => {
    if (restoring) return;

    if (!user) {
      pendingRestoreRef.current = true;
      onSignIn?.();
      return;
    }

    setRestoring(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/cart-recovery/${token}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 401 && data.requiresLogin) {
        pendingRestoreRef.current = true;
        onSignIn?.();
        return;
      }
      if (!res.ok) {
        setState((prev) => ({ ...prev, status: 'error', error: data.error || 'We could not open your saved cart.', reason: data.reason || null }));
        return;
      }

      // The server has already merged and persisted; this hands the same list
      // to the app's own cart state so both agree without a round trip.
      onRestored?.(data.items || []);
      onContinueToCheckout?.();
    } catch {
      setState((prev) => ({ ...prev, status: 'error', error: 'Something went wrong opening your cart. Please try again.', reason: null }));
    } finally {
      setRestoring(false);
    }
  }, [restoring, user, token, onSignIn, onRestored, onContinueToCheckout]);

  // Finishes what the customer started before the login interrupted them.
  useEffect(() => {
    if (user && pendingRestoreRef.current && state.status === 'ready') {
      pendingRestoreRef.current = false;
      restore();
    }
  }, [user, state.status, restore]);

  if (state.status === 'loading') {
    return (
      <div className="checkout-step-pad">
        <p style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          Opening your saved cart…
        </p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="checkout-step-pad">
        <div className="co-panel" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <IconCart size={34} />
          </div>
          <h2 className="co-panel-title" style={{ justifyContent: 'center' }}>Your cart link</h2>
          {/* The server writes this copy so every surface says the same thing,
              and it never mentions tokens, hashes or databases. */}
          <p style={{ color: 'var(--text-secondary)', margin: '8px 0 20px' }}>{state.error}</p>
          <button className="btn btn-place-order" onClick={() => (user ? onContinueToCheckout?.() : onSignIn?.())}>
            {user ? 'Go to my cart' : 'Sign in'} <IconArrowRight size={16} />
          </button>
          <button className="btn-link" style={{ marginTop: 12 }} onClick={() => onGoHome?.()}>
            Continue shopping
          </button>
        </div>
      </div>
    );
  }

  const { items = [], changes = [], removed = [], total = 0, empty } = state.data || {};

  if (empty || items.length === 0) {
    return (
      <div className="checkout-step-pad">
        <div className="co-panel" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><IconCart size={34} /></div>
          <h2 className="co-panel-title" style={{ justifyContent: 'center' }}>These items are no longer available</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '8px 0 20px' }}>
            Everything in this saved cart has sold out or been delisted since we emailed you.
          </p>
          <button className="btn btn-place-order" onClick={() => onGoHome?.()}>
            Browse the store <IconArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-step-pad">
      <div className="order-summary-container">
        <div className="summary-left co-panel">
          <h2 className="co-panel-title">Your saved cart</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 14px' }}>
            We kept these for you. Check them over and continue whenever you’re ready.
          </p>

          <ChangeNotice changes={changes} removed={removed} />

          <div className="order-items">
            {items.map((item) => (
              <CartLine
                key={`${item.productId}-${item.size || ''}-${item.color || ''}`}
                item={item}
                onNavigateToProduct={onNavigateToProduct}
              />
            ))}
          </div>

          <div className="co-protection-note">
            <IconShieldCheck size={15} /> Items and prices are subject to availability until your order is confirmed.
          </div>
        </div>

        <div className="summary-right">
          <div className="price-details co-panel">
            <h2 className="co-panel-title">Price Details</h2>
            <div className="price-row">
              <span>Items ({items.reduce((sum, i) => sum + i.quantity, 0)})</span>
              <span>{formatCurrency(total)}</span>
            </div>
            {/* Delivery is deliberately not estimated here: the real fee is
                zone-based on the delivery address, which this page has not
                asked for yet. Checkout shows it before anything is payable --
                the same reasoning the cart page already applies. */}
            <div className="price-row" style={{ color: 'var(--text-muted)', fontSize: '0.84rem' }}>
              <span>Delivery</span>
              <span>Calculated at checkout</span>
            </div>
            <hr />
            <div className="price-row total">
              <span>Cart total</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>

          <button className="btn btn-place-order" onClick={restore} disabled={restoring}>
            {restoring ? 'Opening your cart…' : <>Complete My Order <IconArrowRight size={16} /></>}
          </button>
          <p className="co-under-cta">
            {user
              ? 'Your cart opens at the order summary, so you can review delivery and place your order without starting again.'
              : 'Sign in to open this cart — you’ll come straight back here.'}
          </p>

          <div className="co-protection-note" style={{ marginTop: 12 }}>
            <IconCheckCircle size={15} /> Secure checkout
          </div>
        </div>
      </div>
    </div>
  );
}
